/** Settings: Worker/model/PIN/privacy/push/E2E self-test/local data. */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { ThinkingEffort, ThinkingMode } from '@luminis/protocol';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { MemorySettingsSection } from '../components/MemorySettingsSection';
import { SessionMonitoringSection } from '../components/SessionMonitoringSection';
import { StatusBanner } from '../components/StatusBanner';
import { relayUrlFromWorkerUrl } from '../lib/api/relay';
import { WorkerClient } from '../lib/api/worker';
import { fingerprint } from '../lib/crypto/e2e';
import { formatClock, shortId } from '../lib/format';
import {
  normalizeDaemonDeviceId,
  normalizeDaemonE2ePublicKey,
  secureCryptoStatus,
} from '../lib/live/plan';
import { relayStatusView } from '../lib/live/status';
import { isValidPin, passkeyProvider } from '../lib/pin';
import {
  disablePushNotifications,
  enablePushNotifications,
  isPushSupported,
  sendPushTest,
  vapidKeyFromHealth,
} from '../lib/push';
import { EFFORT_OPTIONS, MODEL_OPTIONS, PERSONA_OPTIONS, THINKING_OPTIONS } from '../lib/settings';
import { useApp } from '../state/AppState';

const APP_VERSION = '0.1.0';

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-4">
      <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
      {hint ? <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p> : null}
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange(checked: boolean): void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5">
      <span className="flex flex-col">
        <span className="text-sm text-slate-200">{label}</span>
        {hint ? <span className="text-[11px] text-slate-500">{hint}</span> : null}
      </span>
      <input
        type="checkbox"
        className="h-5 w-5 accent-indigo-500"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

export function SettingsPage() {
  const {
    settings,
    updateSettings,
    paired,
    identity,
    pinRecord,
    lock,
    setPin,
    removePin,
    verifyForAction,
    testWorkerConnection,
    e2eSelfTest,
    unpair,
    resetAll,
    relayPlan,
    relayKeyReady,
    retryRelay,
    network,
  } = useApp();
  const [workerUrl, setWorkerUrl] = useState(settings.workerUrl);
  const [workerStatus, setWorkerStatus] = useState<string | null>(null);
  const [daemonDeviceId, setDaemonDeviceId] = useState(settings.daemonDeviceId);
  const [daemonPublicKey, setDaemonPublicKey] = useState(settings.daemonE2ePublicKey);
  const [relayMessage, setRelayMessage] = useState<string | null>(null);
  const [pushHint, setPushHint] = useState<string | null>(
    isPushSupported()
      ? null
      : '当前浏览器不支持 Web Push（需要 HTTPS/localhost；iOS 需先添加到主屏幕）',
  );
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushDiag, setPushDiag] = useState<string | null>(null);
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinMessage, setPinMessage] = useState<string | null>(null);
  const [e2eBusy, setE2eBusy] = useState(false);
  const [e2eResult, setE2eResult] = useState<string | null>(null);
  const [confirmUnpair, setConfirmUnpair] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const secure = secureCryptoStatus();
  const relayView = relayStatusView({
    demoMode: settings.demoMode,
    paired: Boolean(paired),
    plan: relayPlan,
    relayConnected: network.relayConnected,
    ...(network.relayStatus ? { relayStatus: network.relayStatus } : {}),
    keyReady: relayKeyReady,
  });

  useEffect(() => setWorkerUrl(settings.workerUrl), [settings.workerUrl]);
  useEffect(() => setDaemonDeviceId(settings.daemonDeviceId), [settings.daemonDeviceId]);
  useEffect(() => setDaemonPublicKey(settings.daemonE2ePublicKey), [settings.daemonE2ePublicKey]);

  const saveWorkerUrl = (): void => {
    updateSettings({ workerUrl });
    setWorkerStatus(workerUrl.trim() ? '已保存' : '已清空 Worker 地址');
  };

  /**
   * Save the live relay target (daemon device id + X25519 public key). Both are
   * public values; no token/private key is stored here. Invalid input is
   * rejected instead of silently saved.
   */
  const saveRelayConfig = (): void => {
    const rawId = daemonDeviceId.trim();
    const rawKey = daemonPublicKey.trim();
    const deviceId = normalizeDaemonDeviceId(rawId);
    const publicKey = normalizeDaemonE2ePublicKey(rawKey);
    if (rawId && !deviceId) {
      setRelayMessage('daemon device id 格式无效（应为 dev_ 开头的 6-64 位字符）');
      return;
    }
    if (rawKey && !publicKey) {
      setRelayMessage('daemon X25519 公钥无效（需 base64url、解码后 32 字节）');
      return;
    }
    updateSettings({ daemonDeviceId: deviceId, daemonE2ePublicKey: publicKey });
    setDaemonDeviceId(deviceId);
    setDaemonPublicKey(publicKey);
    setRelayMessage(deviceId && publicKey ? '已保存 live relay 配置' : '已清空 live relay 配置');
  };

  const checkWorker = async (): Promise<void> => {
    setWorkerStatus('正在连接…');
    const result = await testWorkerConnection();
    setWorkerStatus(result.message);
  };

  const enablePush = async (): Promise<void> => {
    setPushBusy(true);
    setPushMessage(null);
    try {
      if (!paired) {
        setPushMessage('请先完成设备配对');
        return;
      }
      const baseUrl = settings.workerUrl || paired.worker_url;
      const client = new WorkerClient({ baseUrl });
      const health = await client.health();
      const vapid = vapidKeyFromHealth(health);
      if (!vapid) {
        setPushMessage('Worker 未配置 VAPID 公钥，Web Push 未启用（见 worker README）');
        return;
      }
      const result = await enablePushNotifications({
        workerUrl: baseUrl,
        token: paired.token,
        vapidPublicKey: vapid,
      });
      setPushMessage(result.message);
      if (result.ok) updateSettings({ notificationsEnabled: true });
    } catch (error) {
      setPushMessage(error instanceof Error ? error.message : '推送注册失败');
    } finally {
      setPushBusy(false);
    }
  };

  /**
   * VAPID keys can be rotated on the Worker. Browsers bind an existing push
   * subscription to the applicationServerKey it was created with, so a plain
   * re-subscribe fails with InvalidStateError. Drop the old subscription first.
   */
  const sendRemotePushTest = async (): Promise<void> => {
    setPushBusy(true);
    setPushMessage(null);
    try {
      if (!paired) {
        setPushMessage('请先完成设备配对');
        return;
      }
      const baseUrl = settings.workerUrl || paired.worker_url;
      const result = await sendPushTest({ workerUrl: baseUrl, token: paired.token });
      setPushMessage(result.message);
    } catch (error) {
      setPushMessage(error instanceof Error ? `推送测试失败：${error.message}` : '推送测试失败');
    } finally {
      setPushBusy(false);
    }
  };

  const resetPush = async (): Promise<void> => {
    setPushBusy(true);
    setPushMessage(null);
    try {
      await disablePushNotifications();
      setPushMessage('已清除旧订阅，正在用当前 VAPID 公钥重新注册…');
      await enablePush();
    } catch (error) {
      setPushMessage(error instanceof Error ? error.message : '推送订阅重置失败');
      setPushBusy(false);
    }
  };

  /** Local-only diagnostics: separates OS notification permission/SW issues from Web Push delivery. */
  const refreshPushDiag = async (): Promise<void> => {
    try {
      const permission =
        typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      let endpointHost = 'none';
      if (subscription) {
        try {
          endpointHost = new URL(subscription.endpoint).host;
        } catch {
          endpointHost = 'invalid';
        }
      }
      setPushDiag(
        `permission=${permission}; sw=${registration?.active?.state ?? (registration ? 'registered' : 'none')}; subscription=${endpointHost}; endpoint_len=${subscription?.endpoint.length ?? 0}`,
      );
    } catch (error) {
      setPushDiag(`诊断失败：${error instanceof Error ? error.message : 'unknown'}`);
    }
  };

  const sendLocalTestNotification = async (): Promise<void> => {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification('Luminis 测试通知', {
        body: '如果你看到这条，说明系统通知通道正常',
        icon: './icons/icon-192.png',
        badge: './icons/icon-192.png',
        tag: 'luminis-local-test',
      });
      setPushDiag('已调用 showNotification；请查看手机通知栏');
    } catch (error) {
      setPushDiag(`本地通知失败：${error instanceof Error ? error.message : 'unknown'}`);
    }
  };

  const applyPin = async (): Promise<void> => {
    setPinMessage(null);
    if (newPin !== confirmPin) {
      setPinMessage('两次输入的 PIN 不一致');
      return;
    }
    if (!isValidPin(newPin)) {
      setPinMessage('PIN 必须是 4-8 位数字');
      return;
    }
    if (pinRecord) {
      const verified = await verifyForAction(oldPin);
      if (!verified.ok) {
        setPinMessage(verified.error ?? '原 PIN 不正确');
        return;
      }
    }
    const result = await setPin(newPin);
    if (result.ok) {
      setOldPin('');
      setNewPin('');
      setConfirmPin('');
      setPinMessage('PIN 已保存（高危审批时会要求重新输入）');
    } else {
      setPinMessage(result.error ?? '设置失败');
    }
  };

  const removeExistingPin = async (): Promise<void> => {
    const verified = await verifyForAction(oldPin);
    if (!verified.ok) {
      setPinMessage(verified.error ?? '原 PIN 不正确');
      return;
    }
    await removePin();
    setOldPin('');
    setPinMessage('PIN 已移除');
  };

  const tryPasskey = async (): Promise<void> => {
    const supported = await passkeyProvider.supported();
    if (!supported) {
      setPinMessage('当前浏览器不支持 Passkey');
      return;
    }
    const result = await passkeyProvider.authenticate('设置 Passkey');
    setPinMessage(result.error ?? 'Passkey 操作已取消');
  };

  const runE2eSelfTest = async (): Promise<void> => {
    setE2eBusy(true);
    setE2eResult(null);
    const result = await e2eSelfTest();
    setE2eBusy(false);
    setE2eResult(`${result.ok ? '✅ 通过' : '❌ 未通过'}：${result.detail}`);
  };

  return (
    <div className="h-full overflow-y-auto">
      <StatusBanner />
      <div className="space-y-4 px-4 pb-8">
        {identity ? (
          <Section title="当前设备" hint="私钥仅保存在本机；Worker 只保存设备 token 的哈希">
            <div className="space-y-1 text-xs text-slate-400">
              <p className="flex justify-between gap-3">
                <span>设备名</span>
                <span className="text-slate-200">{identity.name}</span>
              </p>
              <p className="flex justify-between gap-3">
                <span>设备 ID</span>
                <span className="font-mono text-slate-300">{shortId(identity.device_id, 22)}</span>
              </p>
              <p className="flex justify-between gap-3">
                <span>平台</span>
                <span className="text-slate-300">{identity.platform}</span>
              </p>
              <p className="flex justify-between gap-3">
                <span>X25519 公钥</span>
                <span className="font-mono text-slate-300">
                  {fingerprint(identity.e2e_public_key, 2)}
                </span>
              </p>
              <p className="flex justify-between gap-3">
                <span>Ed25519 公钥</span>
                <span className="font-mono text-slate-300">
                  {fingerprint(identity.signing_public_key, 2)}
                </span>
              </p>
              {paired ? (
                <p className="flex justify-between gap-3">
                  <span>配对到期</span>
                  <span className="text-slate-300">{formatClock(paired.expires_at)}</span>
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void (pinRecord ? lock() : setPinMessage('尚未设置 PIN'))}
                className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
              >
                <Icon name="lock" className="h-3.5 w-3.5" />
                立即锁定
              </button>
              <button
                type="button"
                onClick={() => void tryPasskey()}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
              >
                Passkey（预留接口）
              </button>
            </div>
            {pinMessage ? <p className="text-[11px] text-indigo-200">{pinMessage}</p> : null}
          </Section>
        ) : null}

        <Section
          title="Worker 与运行模式"
          hint="聊天走 Worker 的 DeepSeek 代理；Worker 不接触 E2E 审批明文"
        >
          <label className="flex flex-col gap-1 text-xs text-slate-300">
            <span>Worker 地址</span>
            <input
              value={workerUrl}
              onChange={(event) => setWorkerUrl(event.target.value)}
              placeholder="https://wintersrain.workers.dev"
              inputMode="url"
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm outline-none focus:border-indigo-400/60"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveWorkerUrl}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
            >
              保存
            </button>
            <button
              type="button"
              onClick={() => void checkWorker()}
              className="rounded-lg border border-indigo-400/30 bg-indigo-400/10 px-3 py-1.5 text-xs text-indigo-200"
            >
              测试连接
            </button>
          </div>
          {workerStatus ? <p className="text-[11px] text-slate-400">{workerStatus}</p> : null}
          <Toggle
            label="演示模式（Mock daemon）"
            hint="所有数据本地模拟，不连接真实 PC/Worker"
            checked={settings.demoMode}
            onChange={(checked) => updateSettings({ demoMode: checked })}
          />
        </Section>

        <Section
          title="Live relay（E2E 直连 daemon）"
          hint="审批决定/聊天/会话增量经 Worker 中继的 E2E 密文通道；配对成功后自动填入，可手填 fallback"
        >
          <p className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2 text-[11px] leading-relaxed text-slate-400">
            relay URL：
            <span className="ml-1 break-all font-mono text-slate-300">
              {settings.demoMode
                ? '演示模式不连接真实 relay'
                : relayUrlFromWorkerUrl(settings.workerUrl || paired?.worker_url || '') ||
                  '（先填写 Worker 地址）'}
            </span>
            <br />
            当前状态：
            <span
              className={
                relayView.ready
                  ? 'text-emerald-200'
                  : relayView.phase === 'error' || relayView.phase === 'unpaired'
                    ? 'text-rose-200'
                    : 'text-amber-200'
              }
            >
              {relayView.label}
              {relayView.ready
                ? '（E2E 通道就绪）'
                : relayPlan.enabled
                  ? '（E2E 通道连接中）'
                  : `（${relayPlan.reason ?? '未启用'}）`}
            </span>
            {relayView.phase === 'offline' || relayView.phase === 'error' ? (
              <button
                type="button"
                onClick={retryRelay}
                className="ml-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[11px] text-amber-100"
              >
                重试连接
              </button>
            ) : null}
          </p>
          {network.relayDetail ? (
            <p className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2 text-[11px] leading-relaxed text-slate-400">
              {network.relayDetail}
            </p>
          ) : null}
          {!secure.ok ? (
            <p className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
              {secure.reason}。HTTP 局域网下 E2E 加密路径不可用（可用演示模式验收 UI，或改用
              HTTPS/localhost）。
            </p>
          ) : null}
          <label className="flex flex-col gap-1 text-xs text-slate-300">
            <span>daemon device id</span>
            <input
              value={daemonDeviceId}
              onChange={(event) => setDaemonDeviceId(event.target.value)}
              placeholder="dev_xxxxxxxxxxxx"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 font-mono text-sm outline-none focus:border-indigo-400/60"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-300">
            <span>daemon X25519 公钥（base64url，32 字节）</span>
            <input
              value={daemonPublicKey}
              onChange={(event) => setDaemonPublicKey(event.target.value)}
              placeholder="手机的 X25519 私钥 + 该公钥派生 E2E 通道密钥"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 font-mono text-[11px] outline-none focus:border-indigo-400/60"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveRelayConfig}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
            >
              保存 relay 配置
            </button>
          </div>
          {relayMessage ? <p className="text-[11px] text-indigo-200">{relayMessage}</p> : null}
          <p className="text-[10px] leading-relaxed text-slate-500">
            「设备」页的「连接 PC daemon」会通过一次性中继引导配对自动填入 daemon device id 与
            X25519 公钥；本区域保留手填作为 fallback。配置仅存本机 localStorage；daemon 中继桥接
            完成前，live 审批/聊天/会话推送可能暂不可用。演示模式行为不变。
          </p>
        </Section>

        <SessionMonitoringSection />

        <MemorySettingsSection />

        <Section title="人格" hint="选择光耀的说话风格；下一个会话生效">
          <div className="grid gap-2">
            {PERSONA_OPTIONS.map((option) => {
              const active = settings.personaId === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  data-testid={`persona-${option.value}`}
                  onClick={() => updateSettings({ personaId: option.value })}
                  className={`flex items-center justify-between rounded-xl border px-3 py-2.5 text-left text-xs ${
                    active
                      ? 'border-indigo-400/50 bg-indigo-400/10 text-indigo-100'
                      : 'border-white/5 bg-white/[0.03] text-slate-300'
                  }`}
                >
                  <span>
                    <span className="block text-sm">{option.label}</span>
                    <span className="text-[10px] text-slate-500">{option.hint}</span>
                  </span>
                  {active ? <Icon name="check" className="h-4 w-4" /> : null}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] leading-relaxed text-slate-500">
            「可爱光耀」会更软、更活泼；遇到学术、安全或执行任务时会自动收起可爱，不影响可靠性。
          </p>
        </Section>

        <Section
          title="模型与思考强度"
          hint="默认 deepseek-flash + 思考开启 + 最高强度；可在会话中生效"
        >
          <div className="grid gap-2">
            {MODEL_OPTIONS.map((option) => {
              const active = settings.model.model === option.model;
              return (
                <button
                  key={option.model}
                  type="button"
                  onClick={() =>
                    updateSettings({ model: { provider: option.provider, model: option.model } })
                  }
                  className={`flex items-center justify-between rounded-xl border px-3 py-2.5 text-left text-xs ${
                    active
                      ? 'border-indigo-400/50 bg-indigo-400/10 text-indigo-100'
                      : 'border-white/5 bg-white/[0.03] text-slate-300'
                  }`}
                >
                  <span>
                    <span className="block text-sm">{option.label ?? option.model}</span>
                    <span className="text-[10px] text-slate-500">
                      {option.model} · {option.supports_tools ? '支持工具' : '无工具'}
                    </span>
                  </span>
                  {active ? <Icon name="check" className="h-4 w-4" /> : null}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs text-slate-300">
              <span>思考</span>
              <select
                value={settings.thinking}
                onChange={(event) =>
                  updateSettings({ thinking: event.target.value as ThinkingMode })
                }
                className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none"
              >
                {THINKING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-300">
              <span>思考强度</span>
              <select
                value={settings.effort}
                onChange={(event) =>
                  updateSettings({ effort: event.target.value as ThinkingEffort })
                }
                className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none"
              >
                {EFFORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} · {option.hint}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="flex flex-col gap-1 text-xs text-slate-300">
            <span>工具调用轮次上限</span>
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              value={settings.maxToolRounds}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (Number.isInteger(value) && value >= 1 && value <= 50) {
                  updateSettings({ maxToolRounds: value });
                }
              }}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none"
            />
            <span className="text-[10px] text-slate-500">
              默认 16；复杂排查可调高，越高越慢、越费 token。1–50。
            </span>
          </label>
        </Section>

        <Section
          title="PIN 与安全"
          hint="PIN 不做自动锁定（按决策）；高危审批每次重新验证。忘记 PIN 只能清除本地数据。"
        >
          {pinRecord ? (
            <p className="text-[11px] text-emerald-200">已设置 PIN；锁定后需 PIN 解锁。</p>
          ) : (
            <p className="text-[11px] text-amber-200">
              尚未设置 PIN（建议设置，用于解锁与高危审批二次验证）。
            </p>
          )}
          {pinRecord ? (
            <input
              value={oldPin}
              onChange={(event) =>
                setOldPin(event.target.value.replace(/[^0-9]/gu, '').slice(0, 8))
              }
              inputMode="numeric"
              type="password"
              placeholder="当前 PIN"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-center text-sm tracking-[0.4em]"
            />
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <input
              value={newPin}
              onChange={(event) =>
                setNewPin(event.target.value.replace(/[^0-9]/gu, '').slice(0, 8))
              }
              inputMode="numeric"
              type="password"
              placeholder={pinRecord ? '新 PIN' : '设置 PIN（4-8 位）'}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-center text-sm tracking-[0.3em]"
            />
            <input
              value={confirmPin}
              onChange={(event) =>
                setConfirmPin(event.target.value.replace(/[^0-9]/gu, '').slice(0, 8))
              }
              inputMode="numeric"
              type="password"
              placeholder="确认 PIN"
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-center text-sm tracking-[0.3em]"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void applyPin()}
              className="rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-medium text-white"
            >
              {pinRecord ? '修改 PIN' : '设置 PIN'}
            </button>
            {pinRecord ? (
              <button
                type="button"
                onClick={() => void removeExistingPin()}
                className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-1.5 text-xs text-rose-200"
              >
                移除 PIN
              </button>
            ) : null}
          </div>
          {pinMessage ? <p className="text-[11px] text-slate-400">{pinMessage}</p> : null}
        </Section>

        <Section title="通知（Web Push 骨架）" hint="Worker 只发无载荷推送；详情需打开 App 拉取">
          <p className="text-[11px] text-slate-500">{pushHint ?? '支持 Web Push。'}</p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pushBusy}
              onClick={() => void enablePush()}
              className="rounded-lg border border-indigo-400/30 bg-indigo-400/10 px-3 py-1.5 text-xs text-indigo-200 disabled:opacity-40"
            >
              {pushBusy ? '注册中…' : '启用推送'}
            </button>
            <button
              type="button"
              disabled={pushBusy}
              onClick={() => void resetPush()}
              className="rounded-lg border border-slate-500/30 bg-slate-500/10 px-3 py-1.5 text-xs text-slate-300 disabled:opacity-40"
              title="VAPID 公钥轮换后使用：清除旧订阅并按当前公钥重新注册"
            >
              重置推送订阅
            </button>
            <button
              type="button"
              disabled={pushBusy}
              onClick={() => void refreshPushDiag()}
              className="rounded-lg border border-slate-500/30 bg-slate-500/10 px-3 py-1.5 text-xs text-slate-300 disabled:opacity-40"
            >
              推送诊断
            </button>
            <button
              type="button"
              disabled={pushBusy}
              onClick={() => void sendRemotePushTest()}
              className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-xs text-emerald-200 disabled:opacity-40"
              title="让 Worker 真正发一条 Web Push 到本机"
            >
              发送推送测试
            </button>
            <button
              type="button"
              disabled={pushBusy}
              onClick={() => void sendLocalTestNotification()}
              className="rounded-lg border border-slate-500/30 bg-slate-500/10 px-3 py-1.5 text-xs text-slate-300 disabled:opacity-40"
            >
              测试通知
            </button>
          </div>
          {pushDiag ? <p className="text-[11px] text-slate-400">{pushDiag}</p> : null}
          {pushMessage ? <p className="text-[11px] text-slate-300">{pushMessage}</p> : null}
          <p className="text-[10px] text-slate-600">
            真实推送需要：Worker 配置 VAPID keys + HTTPS + 浏览器通知权限；手机需先“添加到主屏幕”。
          </p>
        </Section>

        <Section title="安全自检" hint="本机执行，不上传任何密钥">
          <button
            type="button"
            disabled={e2eBusy}
            onClick={() => void runE2eSelfTest()}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200 disabled:opacity-40"
          >
            {e2eBusy ? '检测中…' : '运行 E2E 加密 / 签名自检'}
          </button>
          {e2eResult ? (
            <p className="text-[11px] leading-relaxed text-slate-300">{e2eResult}</p>
          ) : null}
        </Section>

        <Section title="数据与关于">
          <div className="space-y-1 text-[11px] text-slate-500">
            <p>Luminis PWA v{APP_VERSION} · protocol v1</p>
            <p>Cloudflare Worker + Vite/React PWA · M0.1–M0.2 骨架</p>
            <p>聊天记录与密钥只保存在本机浏览器存储；无云端备份。</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {paired ? (
              <button
                type="button"
                onClick={() => setConfirmUnpair(true)}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
              >
                解除配对
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-1.5 text-xs text-rose-200"
            >
              清除全部本地数据
            </button>
          </div>
        </Section>
      </div>

      <ConfirmDialog
        open={confirmUnpair}
        title="解除配对？"
        danger
        confirmLabel="解除配对"
        onCancel={() => setConfirmUnpair(false)}
        onConfirm={() => {
          unpair();
          setConfirmUnpair(false);
        }}
      >
        <p>将清除设备令牌与审批缓存；PIN 和聊天记录保留。</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmReset}
        title="清除全部本地数据？"
        danger
        confirmLabel="清除并重置"
        onCancel={() => setConfirmReset(false)}
        onConfirm={() => {
          resetAll();
          setConfirmReset(false);
        }}
      >
        <p>将删除设备密钥、PIN、聊天记录、审批缓存与设置，无法恢复。建议先确认没有未处理的审批。</p>
      </ConfirmDialog>
    </div>
  );
}
